// CadPreviewPage — CAD Assembly Preview with tree visualization + 3D viewer
// Renders parsed STEP/IGES assembly hierarchy, materials, and quantities
import React, { useState, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import ProcessingChoice, { useProcessingChoice } from '../../components/bom-import/ProcessingChoice';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Alert,
  Stack,
  Drawer,
  Divider,
  IconButton,
  LinearProgress,
  CircularProgress,
  Collapse,
  alpha,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  AccountTree as TreeIcon,
  ViewInAr as CadIcon,
  ThreeDRotation as ThreeDIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Settings as GearIcon,
  FolderOpen as FolderIcon,
  Science as MaterialIcon,
  Straighten as GeometryIcon,
  Speed as ConfidenceIcon,
} from '@mui/icons-material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { bomIntelligenceService } from '../../services/bomIntelligenceService';
import { semanticColors } from '../../theme/theme';
import type { CadConversionResult } from '../../services/bomIntelligenceService';
import type { AnalysisPreview, CadPart, CadParseResult, CadMetadata } from '../../types';

const CADViewer = lazy(() => import('../../components/cad-viewer/CADViewer'));

interface CadPreviewPageProps {
  preview: AnalysisPreview;
  onRefresh: () => void;
}

const CadPreviewPage: React.FC<CadPreviewPageProps> = ({ preview, onRefresh }) => {
  const navigate = useNavigate();

  const [selectedPart, setSelectedPart] = useState<CadPart | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // BOM name is captured on upload and carried via the analysis job —
  // fall back to the CAD-extracted assembly name or filename if the user didn't enter one.
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showWarnings, setShowWarnings] = useState(false);

  // 3D viewer state
  const [snapshotDataUrl, setSnapshotDataUrl] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [conversionResult, setConversionResult] = useState<CadConversionResult | null>(null);
  const [stlUrl, setStlUrl] = useState<string | null>(null);
  const [show3D, setShow3D] = useState(false);

  const handleConvertTo3D = async () => {
    if (!preview.analysisId) return;
    setConverting(true);
    setError(null);
    try {
      const result = await bomIntelligenceService.convertCadToStl(preview.analysisId);
      setConversionResult(result);
      if (result.success && result.stl_filename) {
        const url = bomIntelligenceService.getStlUrl(result.stl_filename);
        setStlUrl(url);
        setShow3D(true);
      } else if (!result.has_geometry) {
        setError('This CAD file contains assembly structure only — no 3D geometry to visualize. Real CAD files from tools like SolidWorks or Fusion 360 will include geometry.');
      } else {
        setError(result.error || 'Conversion failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to convert to 3D');
    } finally {
      setConverting(false);
    }
  };

  const cadResult = preview.cadParseResult;
  const cadMeta = preview.cadMetadata;

  // Normalize field names (backend may use camelCase or snake_case)
  const assemblyName = cadResult?.assemblyName || cadResult?.assembly_name || 'Unknown Assembly';
  const totalParts = cadResult?.totalParts ?? cadResult?.total_parts ?? preview.totalRows ?? 0;
  const uniqueParts = cadResult?.uniqueParts ?? cadResult?.total_unique_parts ?? totalParts;
  const hierarchy = cadResult?.hierarchy || [];
  const warnings = cadResult?.warnings || [];
  const errors = cadResult?.errors || [];
  const format = cadMeta?.format || cadResult?.format || preview.fileType || '';

  const handleSelectPart = (part: CadPart) => {
    setSelectedPart(part);
    setDrawerOpen(true);
  };

  const [processing, setProcessing] = useProcessingChoice();

  const handleCreateBom = async () => {
    if (!preview.analysisId) return;
    const resolvedBomName = (
      (preview as any).bomName
      || cadResult?.assemblyName
      || cadResult?.assembly_name
      || preview.originalFilename?.replace(/\.[^.]+$/, '')
      || 'Untitled BOM'
    ).toString().trim();

    setCreating(true);
    try {
      const result = await bomIntelligenceService.confirmAndCreateBom(
        preview.analysisId,
        resolvedBomName,
        {
          bomType: (preview as any).bomType,
          description: (preview as any).description,
          processing,
        },
      );

      // Save the captured 3D snapshot as the BOM thumbnail
      if (snapshotDataUrl && result.bomId) {
        try {
          await bomIntelligenceService.saveThumbnail(result.bomId, snapshotDataUrl);
        } catch {
          // Non-critical — thumbnail save failure should not block navigation
        }
      }

      // Kick off enrichment in the background, then land directly on the BOM's
      // Baseline tab — skip the intermediate Intelligence dashboard. The
      // dashboard stays reachable via the Intelligence icon on the BOM header.
      bomIntelligenceService.enrichAll(result.bomId).catch(() => {
        /* fire-and-forget — the pipeline status strip on the BOM page surfaces failures */
      });
      navigate(`/bom/${result.bomId}`, {
        state: { selectBaseline: true },
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create BOM');
    } finally {
      setCreating(false);
    }
  };

  // Count total line items (flattened parts for BOM)
  const lineItemCount = preview.validRows ?? preview.totalRows ?? 0;

  const getConfidenceDot = (score: number) => {
    const color = score >= 0.7 ? '#2ECC71' : score >= 0.4 ? semanticColors.confidence.medium : '#E74C3C';
    return (
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: color,
          flexShrink: 0,
        }}
      />
    );
  };

  const getConfidenceLabel = (score: number) => {
    if (score >= 0.7) return { text: 'High', color: '#2ECC71' };
    if (score >= 0.4) return { text: 'Medium', color: semanticColors.confidence.medium };
    return { text: 'Low', color: '#E74C3C' };
  };

  const renderTree = (part: CadPart, parentPath: string, index: number): React.ReactNode => {
    const itemId = `${parentPath}-${index}`;
    const isAssembly = part.type === 'ASSEMBLY';

    return (
      <TreeItem
        key={itemId}
        itemId={itemId}
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.5,
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleSelectPart(part);
            }}
          >
            {isAssembly ? (
              <FolderIcon sx={{ fontSize: 18, color: semanticColors.accent.main }} />
            ) : (
              <GearIcon sx={{ fontSize: 16, color: '#64748b' }} />
            )}
            <Typography
              variant="body2"
              fontWeight={isAssembly ? 600 : 400}
              sx={{ color: '#0D1137' }}
            >
              {part.name}
            </Typography>
            {part.quantity > 1 && (
              <Chip
                label={`x${part.quantity}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  bgcolor: alpha('#3B82F6', 0.1),
                  color: '#3B82F6',
                }}
              />
            )}
            {part.manufacturer && (
              <Chip
                label={part.manufacturer}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: alpha('#8B5CF6', 0.1),
                  color: '#8B5CF6',
                  maxWidth: 140,
                }}
              />
            )}
            {part.material && (
              <Chip
                label={part.material}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.6rem',
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  maxWidth: 180,
                }}
              />
            )}
            {part.part_number && !isAssembly && (
              <Typography variant="caption" sx={{ color: '#94A3B8', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem' }}>
                {part.part_number}
              </Typography>
            )}
            <Box sx={{ ml: 'auto' }}>
              {getConfidenceDot(part.confidence.overall)}
            </Box>
          </Box>
        }
      >
        {part.children.map((child, ci) => renderTree(child, itemId, ci))}
      </TreeItem>
    );
  };

  // Collect all expanded item IDs (expand first 2 levels by default)
  const defaultExpanded = useMemo(() => {
    const ids: string[] = [];
    hierarchy.forEach((part, i) => {
      const rootId = `root-${i}`;
      ids.push(rootId);
      part.children.forEach((_child, ci) => {
        ids.push(`${rootId}-${ci}`);
      });
    });
    return ids;
  }, [hierarchy]);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/bom-intelligence/upload')}>
            <BackIcon />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#0D1137' }}>
                CAD Assembly Preview
              </Typography>
              <Chip
                label={format}
                size="small"
                sx={{
                  bgcolor: alpha('#9B59B6', 0.1),
                  color: '#9B59B6',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {preview.originalFilename || assemblyName}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2}>
          <ProcessingChoice value={processing} onChange={setProcessing} />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            sx={{ borderColor: '#cbd5e1', color: '#64748b' }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={creating ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleCreateBom}
            disabled={lineItemCount === 0 || creating}
            sx={{
              bgcolor: semanticColors.accent.main,
              '&:hover': { bgcolor: semanticColors.accent.dark },
            }}
          >
            {creating ? 'Creating...' : `Create BOM (${lineItemCount} items)`}
          </Button>
        </Stack>
      </Box>

      {/* Summary Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Paper elevation={0} sx={{ p: 2, flex: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>
            Total Parts
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#0D1137' }}>
            {totalParts}
          </Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, flex: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>
            Unique Parts
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#3B82F6' }}>
            {uniqueParts}
          </Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, flex: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>
            Materials Found
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#2ECC71' }}>
            {cadMeta?.materialCount ?? 0}
          </Typography>
        </Paper>
        <Paper elevation={0} sx={{ p: 2, flex: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>
            Assemblies
          </Typography>
          <Typography variant="h5" fontWeight={700} sx={{ color: semanticColors.accent.main }}>
            {cadMeta?.assemblyCount ?? 0}
          </Typography>
        </Paper>
      </Stack>

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            warnings.length > 1 ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => setShowWarnings(!showWarnings)}
              >
                {showWarnings ? 'Hide' : `+${warnings.length - 1} more`}
              </Button>
            ) : undefined
          }
        >
          {warnings[0]}
          <Collapse in={showWarnings}>
            {warnings.slice(1).map((w, i) => (
              <Typography key={i} variant="body2" sx={{ mt: 0.5 }}>
                {w}
              </Typography>
            ))}
          </Collapse>
        </Alert>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {errors.map((e, i) => (
            <Typography key={i} variant="body2">
              {e}
            </Typography>
          ))}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 3D Viewer Section */}
      <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: show3D ? '1px solid #e2e8f0' : 'none', display: 'flex', alignItems: 'center', gap: 2 }}>
          <ThreeDIcon sx={{ color: '#9B59B6' }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#0D1137' }}>
            3D Visualization
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            {show3D ? (
              <Button
                size="small"
                onClick={() => setShow3D(false)}
                sx={{ color: '#64748b' }}
              >
                Hide
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={converting ? <CircularProgress size={14} /> : <ThreeDIcon />}
                onClick={stlUrl ? () => setShow3D(true) : handleConvertTo3D}
                disabled={converting}
                sx={{
                  borderColor: '#9B59B6',
                  color: '#9B59B6',
                  '&:hover': { borderColor: '#8E44AD', bgcolor: alpha('#9B59B6', 0.04) },
                }}
              >
                {converting ? 'Converting...' : stlUrl ? 'Show 3D Model' : 'Generate 3D Preview'}
              </Button>
            )}
          </Box>
        </Box>
        {show3D && stlUrl && (
          <Box sx={{ height: 450 }}>
            <Suspense
              fallback={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <CircularProgress sx={{ color: '#9B59B6' }} />
                </Box>
              }
            >
              <CADViewer
                source={stlUrl}
                format="stl"
                height={450}
                showControls
                onSnapshot={(dataUrl) => setSnapshotDataUrl(dataUrl)}
              />
            </Suspense>
          </Box>
        )}
        {show3D && conversionResult?.success && (
          <Box sx={{ p: 1.5, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {conversionResult.faces} faces
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {conversionResult.solids} solids
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              {((conversionResult.file_size_bytes || 0) / 1024).toFixed(1)} KB
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Converted in {conversionResult.conversion_duration_ms}ms
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Assembly Tree */}
      <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
          <TreeIcon sx={{ color: semanticColors.accent.main }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#0D1137' }}>
            Assembly Hierarchy
          </Typography>
          <Chip
            label={`${hierarchy.length} root ${hierarchy.length === 1 ? 'assembly' : 'assemblies'}`}
            size="small"
            sx={{ bgcolor: alpha(semanticColors.accent.main, 0.1), color: semanticColors.accent.main }}
          />
          <Typography variant="caption" sx={{ color: '#94a3b8', ml: 'auto' }}>
            Click any item to view details
          </Typography>
        </Box>

        <Box sx={{ p: 2, maxHeight: 600, overflow: 'auto' }}>
          {hierarchy.length > 0 ? (
            <SimpleTreeView
              defaultExpandedItems={defaultExpanded}
              sx={{
                '& .MuiTreeItem-content': {
                  borderRadius: 1,
                  '&:hover': { bgcolor: alpha(semanticColors.accent.main, 0.04) },
                },
                '& .MuiTreeItem-content.Mui-selected': {
                  bgcolor: alpha(semanticColors.accent.main, 0.08),
                },
              }}
            >
              {hierarchy.map((part, i) => renderTree(part, 'root', i))}
            </SimpleTreeView>
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CadIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                No assembly hierarchy found
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Extracted Materials */}
      {cadMeta?.extractedMaterials && cadMeta.extractedMaterials.length > 0 && (
        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <MaterialIcon sx={{ color: '#2ECC71', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#0D1137' }}>
              Extracted Materials
            </Typography>
            <Chip
              label={cadMeta.extractedMaterials.length}
              size="small"
              sx={{ bgcolor: alpha('#2ECC71', 0.1), color: '#2ECC71' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {cadMeta.extractedMaterials.map((mat, i) => (
              <Chip
                key={i}
                label={mat}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#e2e8f0',
                  color: '#475569',
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Part Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: 400, p: 3 },
        }}
      >
        {selectedPart && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#0D1137' }}>
                Part Details
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Identification */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                {selectedPart.type === 'ASSEMBLY' ? (
                  <FolderIcon sx={{ color: semanticColors.accent.main }} />
                ) : (
                  <GearIcon sx={{ color: '#64748b' }} />
                )}
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0D1137' }}>
                  {selectedPart.name}
                </Typography>
              </Box>
              <Stack spacing={1}>
                <DetailRow label="Type" value={selectedPart.type} />
                {selectedPart.part_number && (
                  <DetailRow label="Part Number" value={selectedPart.part_number} mono />
                )}
              </Stack>
            </Paper>

            {/* Quantity & Material */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Typography variant="caption" fontWeight={600} sx={{ color: '#94a3b8', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                Quantity & Material
              </Typography>
              <Stack spacing={1}>
                <DetailRow label="Quantity" value={String(selectedPart.quantity)} />
                <DetailRow
                  label="Material"
                  value={selectedPart.material || 'Not specified'}
                  color={selectedPart.material ? '#2ECC71' : '#94a3b8'}
                />
                {selectedPart.color && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', width: 100 }}>Color</Typography>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: selectedPart.color,
                        border: '1px solid #e2e8f0',
                      }}
                    />
                    <Typography variant="body2">{selectedPart.color}</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Geometry */}
            {selectedPart.geometry && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <GeometryIcon sx={{ fontSize: 18, color: '#3B82F6' }} />
                  <Typography variant="caption" fontWeight={600} sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>
                    Geometry
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {selectedPart.geometry.volume_cm3 != null && (
                    <DetailRow label="Volume" value={`${selectedPart.geometry.volume_cm3.toFixed(2)} cm3`} />
                  )}
                  {selectedPart.geometry.mass_kg != null && (
                    <DetailRow label="Mass" value={`${selectedPart.geometry.mass_kg.toFixed(4)} kg`} />
                  )}
                  {selectedPart.geometry.surface_area_cm2 != null && (
                    <DetailRow label="Surface Area" value={`${selectedPart.geometry.surface_area_cm2.toFixed(2)} cm2`} />
                  )}
                  {selectedPart.geometry.bounding_box && (
                    <DetailRow
                      label="Bounding Box"
                      value={`${selectedPart.geometry.bounding_box.x} x ${selectedPart.geometry.bounding_box.y} x ${selectedPart.geometry.bounding_box.z} mm`}
                    />
                  )}
                </Stack>
              </Paper>
            )}

            {/* Confidence Scores */}
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <ConfidenceIcon sx={{ fontSize: 18, color: semanticColors.accent.main }} />
                <Typography variant="caption" fontWeight={600} sx={{ color: '#94a3b8', textTransform: 'uppercase' }}>
                  Confidence Scores
                </Typography>
              </Box>
              <Stack spacing={1.5}>
                <ConfidenceBar label="Part Number" value={selectedPart.confidence.part_number} />
                <ConfidenceBar label="Material" value={selectedPart.confidence.material} />
                <ConfidenceBar label="Quantity" value={selectedPart.confidence.quantity} />
                <Divider />
                <ConfidenceBar label="Overall" value={selectedPart.confidence.overall} bold />
              </Stack>
            </Paper>

            {/* Children count */}
            {selectedPart.children.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: alpha(semanticColors.accent.main, 0.05), borderRadius: 2 }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Contains <strong>{selectedPart.children.length}</strong> child {selectedPart.children.length === 1 ? 'component' : 'components'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

// Helper components

const DetailRow: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
  color?: string;
}> = ({ label, value, mono, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <Typography variant="caption" sx={{ color: '#94a3b8', width: 100, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={500}
      sx={{
        color: color || '#0D1137',
        fontFamily: mono ? 'monospace' : 'inherit',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const ConfidenceBar: React.FC<{
  label: string;
  value: number;
  bold?: boolean;
}> = ({ label, value, bold }) => {
  const pct = Math.round(value * 100);
  const color = value >= 0.7 ? '#2ECC71' : value >= 0.4 ? semanticColors.confidence.medium : '#E74C3C';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography
          variant="caption"
          fontWeight={bold ? 700 : 400}
          sx={{ color: '#64748b' }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ color }}
        >
          {pct}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: bold ? 8 : 6,
          borderRadius: 3,
          bgcolor: alpha(color, 0.1),
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
        }}
      />
    </Box>
  );
};

export default CadPreviewPage;
